import { faFloppyDisk, faPen, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { MuiTelInput, matchIsValidTel } from 'mui-tel-input';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import CheckPermissions from '@/components/CheckPermissions';
import LargeCard from '@/components/LargeCard';
import { PERMISSIONS } from '@/constants/auth.constants';

import useStyles from './ContactInformation.styles';
import type {
  ContactInformationFormValues,
  ContactInformationProps
} from './ContactInformation.types';

const ContactInformation = (props: ContactInformationProps) => {
  const { classes } = useStyles();
  const { orgInfo, handleSave } = props;
  const [editing, setIsEditing] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState(false);

  const defaultFormValues = {
    contact_name: orgInfo.contact_name || '',
    contact_email: orgInfo.contact_email || '',
    contact_phone_number: orgInfo.contact_phone_number || ''
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty }
  } = useForm<ContactInformationFormValues>({
    defaultValues: defaultFormValues,
    values: defaultFormValues,
    mode: 'onSubmit',
    reValidateMode: 'onSubmit'
  });

  const onSubmit = async (data: ContactInformationFormValues) => {
    if (isSaving) return;

    setIsSaving(true);
    if (!isDirty) {
      setIsEditing(false);
      setIsSaving(false);
      return;
    }

    try {
      await handleSave({
        ...orgInfo,
        contact_name: data.contact_name,
        contact_email: data.contact_email || null,
        contact_phone_number: data.contact_phone_number || null
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving contact information:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <LargeCard
      title="Client Admin Contact Information"
      rightContent={
        <CheckPermissions permissions={PERMISSIONS.ADMIN_UPDATE}>
          {editing ? (
            <Stack direction="row">
              <IconButton onClick={handleSubmit(onSubmit)} disabled={isSaving}>
                <FontAwesomeIcon size="xs" icon={faFloppyDisk} />
              </IconButton>
              <IconButton
                onClick={() => {
                  setIsEditing(false);
                  reset(defaultFormValues);
                }}
              >
                <FontAwesomeIcon size="xs" icon={faXmark} />
              </IconButton>
            </Stack>
          ) : (
            <IconButton onClick={() => setIsEditing(true)}>
              <FontAwesomeIcon size="xs" icon={faPen} />
            </IconButton>
          )}
        </CheckPermissions>
      }
    >
      <Typography sx={{ pb: 2 }}>
        This section collects the client&apos;s admin contact information. The
        admin contact will receive email notifications for new tickets and when
        new reports are available.
      </Typography>
      <Grid container rowSpacing={0} columnSpacing={4}>
        <Grid size={{ sm: 12, md: 4 }}>
          <InputLabel htmlFor="contact_name">Contact Name</InputLabel>
          <Controller
            name="contact_name"
            control={control}
            render={({ field }) => (
              <TextField
                disabled={!editing}
                className={classes.textField}
                fullWidth
                aria-labelledby="contact_name"
                variant="outlined"
                error={!!errors.contact_name}
                {...field}
              />
            )}
          />
        </Grid>
        <Grid size={{ sm: 12, md: 4 }}>
          <InputLabel htmlFor="contact_email">Contact Email</InputLabel>
          <Controller
            name="contact_email"
            control={control}
            rules={{
              validate: (value) => {
                if (!value || value.length === 0) return true; // Blank email is accepted
                const validEmail = value.includes('@') && value.includes('.');
                return validEmail || 'Please enter a valid email address';
              }
            }}
            render={({ field }) => (
              <TextField
                disabled={!editing}
                className={classes.textField}
                fullWidth
                aria-labelledby="contact_email"
                variant="outlined"
                error={!!errors.contact_email}
                {...field}
              />
            )}
          />
        </Grid>
        <Grid size={{ sm: 12, md: 4 }}>
          <InputLabel htmlFor="contact_phone_number">Phone Number</InputLabel>
          <Controller
            name="contact_phone_number"
            control={control}
            rules={{
              validate: (value) => {
                if (!value || value.length === 0) return true; // Blank phone number is accepted
                const validNumber = matchIsValidTel(value);
                return validNumber || 'Please enter a valid phone number';
              }
            }}
            render={({ field }) => (
              <>
                <MuiTelInput
                  className={classes.textField}
                  disabled={!editing}
                  error={!!errors.contact_phone_number}
                  defaultCountry="US"
                  sx={{ marginBottom: '0 !important' }}
                  {...field}
                />
                {errors.contact_phone_number && (
                  <FormHelperText error>
                    {errors.contact_phone_number.message}
                  </FormHelperText>
                )}
              </>
            )}
          />
        </Grid>
      </Grid>
    </LargeCard>
  );
};

export default ContactInformation;
